import React, { useMemo, useState } from "react";
import { MACHINES, MACHINE_ERAS } from "@/data/maschinen";
import * as Phosphor from "@phosphor-icons/react";

export default function Maschinen() {
  const [era, setEra] = useState("klassik");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return MACHINES.filter((m) => m.era === era).filter((m) => {
      if (!q) return true;
      return [m.title, m.summary, m.details, m.benefit, m.category, (m.brands || []).join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [era, search]);

  const currentEra = MACHINE_ERAS.find((e) => e.id === era);

  return (
    <div className="space-y-6" data-testid="maschinen-root">
      <header>
        <div className="eid-label">Landtechnik · Klassik bis Zukunft</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">
          Landmaschinen-Lexikon
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Vom Pflug zur Drohne: alle wichtigen Maschinen, die in heimischen
          Betrieben heute stehen — und ein Blick auf die Technologien, die
          in den nächsten 5 Jahren den Alltag verändern.
        </p>
      </header>

      {/* Era tabs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="maschinen-eras">
        {MACHINE_ERAS.map((e) => {
          const active = e.id === era;
          const count = MACHINES.filter((m) => m.era === e.id).length;
          return (
            <button
              key={e.id}
              onClick={() => setEra(e.id)}
              data-testid={`maschinen-era-${e.id}`}
              className={`text-left rounded-sm border p-3 transition-all ${
                active ? "shadow-sm" : "border-white/10 hover:bg-white/5"
              }`}
              style={
                active
                  ? { background: e.color, color: "#FFFFFF", borderColor: e.color }
                  : { background: "#121212" }
              }
            >
              <div className="text-xs font-mono uppercase tracking-wider opacity-80">
                {count} Einträge
              </div>
              <div className="text-lg font-medium mt-0.5">{e.title}</div>
              <div className="text-xs opacity-90 mt-0.5">{e.subtitle}</div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="eid-card p-3 flex items-center gap-2">
        <Phosphor.MagnifyingGlass size={16} className="text-zinc-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="maschinen-search"
          placeholder='Suche: z.B. „Drohne", „John Deere", „Pflug"…'
          className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-zinc-500"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            data-testid="maschinen-clear"
            className="text-zinc-500 hover:text-white"
          >
            <Phosphor.X size={14} />
          </button>
        )}
      </div>

      {/* Grid */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="maschinen-grid">
        {filtered.length === 0 && (
          <div className="md:col-span-2 eid-card p-6 text-sm text-zinc-400">
            Keine Treffer für „{search}" in {currentEra?.title}.
          </div>
        )}
        {filtered.map((m) => (
          <MachineCard key={m.id} machine={m} accent={currentEra.color} />
        ))}
      </section>
    </div>
  );
}

function MachineCard({ machine, accent }) {
  const [open, setOpen] = useState(false);
  const Icon = Phosphor[machine.icon] || Phosphor.Wrench;
  return (
    <article
      className="eid-card overflow-hidden flex flex-col"
      data-testid={`maschinen-card-${machine.id}`}
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div
        className="aspect-[16/9] relative overflow-hidden flex items-center justify-center"
        style={{ background: machine.gradient }}
      >
        <Icon size={96} weight="thin" color="rgba(255,255,255,0.35)" />
        <span
          className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20 backdrop-blur text-white border border-white/30"
        >
          {machine.category}
        </span>
      </div>
      <div className="p-4 md:p-5 flex-1 flex flex-col">
        <h3 className="text-xl font-medium tracking-tight">{machine.title}</h3>
        <p className="text-sm text-zinc-400 mt-1">{machine.summary}</p>

        {open && (
          <div className="mt-3 space-y-3 text-sm" data-testid={`maschinen-body-${machine.id}`}>
            <div className="text-white leading-relaxed">{machine.details}</div>
            <div
              className="p-3 rounded-sm flex gap-2"
              style={{ background: `${accent}14`, borderLeft: `3px solid ${accent}` }}
            >
              <Phosphor.Lightbulb size={16} style={{ color: accent }} className="shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: accent }}>
                  Warum wichtig?
                </div>
                <div className="text-sm text-white mt-0.5">{machine.benefit}</div>
              </div>
            </div>
            {machine.brands?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 self-center">
                  Hersteller:
                </span>
                {machine.brands.map((b) => (
                  <span
                    key={b}
                    className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-white/10 text-zinc-400"
                  >
                    {b}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={`maschinen-toggle-${machine.id}`}
          className="mt-3 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white self-start flex items-center gap-1"
        >
          {open ? "Weniger" : "Details zeigen"}
          <Phosphor.CaretDown
            size={12}
            style={{ transform: open ? "rotate(180deg)" : "rotate(0)" }}
            className="transition-transform"
          />
        </button>
      </div>
    </article>
  );
}
